import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, Store, Search } from "lucide-react";
import { LLink, LNavLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";

const ONTDEKKEN = [
  { key: "eten-drinken", tKey: "eten_drinken", to: "/eten-en-drinken" },
  { key: "winkelen", tKey: "winkelen", to: "/winkelen" },
  { key: "mode", tKey: "mode", to: "/mode" },
  { key: "wonen-interieur", tKey: "wonen_interieur", to: "/wonen-interieur" },
  { key: "gezondheid-beauty", tKey: "gezondheid_beauty", to: "/gezondheid-beauty" },
  { key: "diensten", tKey: "diensten", to: "/diensten" },
  { key: "overnachten", tKey: "overnachten", to: "/overnachten" },
  { key: "cultuur-vrije-tijd", tKey: "cultuur_vrije_tijd", to: "/cultuur-vrije-tijd" },
];

const TOP_TAIL = [
  { key: "bedrijvengids", tKey: "bedrijvengids", to: "/bedrijvengids" },
  { key: "agenda", tKey: "agenda", to: "/agenda" },
  { key: "nieuws", tKey: "nieuws", to: "/nieuws" },
  { key: "webcams", tKey: "webcams", to: "/webcams" },
  { key: "praktisch", tKey: "praktisch", to: "/praktisch" },
  { key: "over-ons", tKey: "over_ons", to: "/over-ons" },
];

const LANGUAGES = [
  { code: "nl", display: "NL" },
  { code: "de", display: "DE" },
  { code: "en", display: "EN" },
];

export const Header = () => {
  const { t } = useTranslation();
  const { lang, switchLang, prefix } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ontdekkenOpen, setOntdekkenOpen] = useState(false);
  const [mobileOntdekkenOpen, setMobileOntdekkenOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const q = searchQ.trim();
    const target = `${prefix}/bedrijvengids${q ? `?q=${encodeURIComponent(q)}` : ""}`;
    navigate(target);
    setSearchQ("");
    setSearchOpen(false);
    setMobileOpen(false);
  };

  // Auto-focus wanneer het veld opent (na eerste frame zodat transitie soepel start)
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 40);
      return () => window.clearTimeout(id);
    }
  }, [searchOpen]);

  // Klik-buiten sluit het zoekveld
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onDocMouseDown = (ev) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(ev.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [searchOpen]);

  const closeAll = () => {
    setMobileOpen(false);
    setOntdekkenOpen(false);
    setMobileOntdekkenOpen(false);
    setLangOpen(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[#E4EEF5]"
    >
      <div className="ll-container flex items-center gap-4 h-20 sm:h-24">
        {/* Logo */}
        <LLink
          to="/"
          data-testid="site-logo"
          className="flex items-center shrink-0"
          onClick={closeAll}
        >
          <img
            src="/logos/header-lemmer-leeft.png"
            alt="Lemmer Leeft"
            className="h-11 sm:h-14 w-auto"
          />
        </LLink>

        {/* Desktop nav */}
        <nav
          data-testid="desktop-nav"
          className="hidden lg:flex items-center gap-1 xl:gap-2 ml-auto"
        >
          <LNavLink
            to="/"
            end
            data-testid="nav-link-home"
            className={({ isActive }) =>
              `ll-nav-link text-sm px-3 py-2 whitespace-nowrap ${
                isActive ? "active" : ""
              }`
            }
          >
            {t("nav.home")}
          </LNavLink>

          {/* Ontdekken dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setOntdekkenOpen(true)}
            onMouseLeave={() => setOntdekkenOpen(false)}
          >
            <button
              type="button"
              data-testid="nav-ontdekken"
              onClick={() => setOntdekkenOpen((s) => !s)}
              className="ll-nav-link text-sm px-3 py-2 whitespace-nowrap inline-flex items-center gap-1"
              aria-haspopup="menu"
              aria-expanded={ontdekkenOpen}
            >
              {t("nav.ontdekken")}
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  ontdekkenOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {ontdekkenOpen && (
              <div
                data-testid="ontdekken-dropdown"
                className="absolute top-full left-0 pt-2 min-w-[220px] z-50"
              >
                <div className="bg-white rounded-xl shadow-xl border border-[#E4EEF5] py-1.5">
                  {ONTDEKKEN.map((item) => (
                    <LNavLink
                      key={item.to}
                      to={item.to}
                      data-testid={`ontdekken-item-${item.key}`}
                      onClick={() => setOntdekkenOpen(false)}
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm font-[Poppins] font-medium transition ${
                          isActive
                            ? "text-[#2E9CD6] bg-[#F4F8FB]"
                            : "text-[#173A5E] hover:bg-[#F4F8FB]"
                        }`
                      }
                    >
                      {t(`nav.${item.tKey}`)}
                    </LNavLink>
                  ))}
                </div>
              </div>
            )}
          </div>

          {TOP_TAIL.map((item) => (
            <LNavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                `ll-nav-link text-sm px-3 py-2 whitespace-nowrap ${
                  isActive ? "active" : ""
                }`
              }
            >
              {t(`nav.${item.tKey}`)}
            </LNavLink>
          ))}
        </nav>

        {/* Right: Voor ondernemers CTA + search + language + hamburger */}
        <div className="flex items-center gap-2 ml-auto lg:ml-2">
          {/* Uitklap-zoek: icoon-only, opent inline zoekveld met focus */}
          <div ref={searchWrapRef} className="relative">
            <form
              onSubmit={submitSearch}
              role="search"
              data-testid="header-search-form"
              className="flex items-center"
            >
              <div
                className={`overflow-hidden transition-[width,opacity,margin] duration-300 ease-out ${
                  searchOpen
                    ? "w-40 sm:w-56 lg:w-64 opacity-100 mr-1.5"
                    : "w-0 opacity-0 mr-0"
                }`}
                aria-hidden={!searchOpen}
              >
                <input
                  ref={searchInputRef}
                  data-testid="header-search-input"
                  type="search"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setSearchQ("");
                      setSearchOpen(false);
                    }
                  }}
                  onBlur={() => {
                    // Sluit als het veld leeg is en focus vertrekt.
                    // Timeout zodat een klik op het vergrootglas (submit) niet gemist wordt.
                    if (!searchQ.trim()) {
                      window.setTimeout(() => setSearchOpen(false), 120);
                    }
                  }}
                  placeholder={t("common.zoeken")}
                  aria-label={t("common.zoeken")}
                  tabIndex={searchOpen ? 0 : -1}
                  className="w-full bg-[#F4F8FB] focus:bg-white border border-[#E4EEF5] focus:border-[#6BC0E8] rounded-full px-4 py-2 outline-none text-sm font-[Inter] text-[#173A5E] placeholder:text-[#8AA3B8] transition-colors"
                />
              </div>
              <button
                type={searchOpen ? "submit" : "button"}
                onClick={(e) => {
                  if (!searchOpen) {
                    e.preventDefault();
                    setSearchOpen(true);
                  }
                }}
                data-testid="header-search-toggle"
                aria-label={t("common.zoeken")}
                aria-expanded={searchOpen}
                className="p-2 rounded-full hover:bg-[#F4F8FB] transition text-[#173A5E]"
              >
                <Search className="w-5 h-5" />
              </button>
            </form>
          </div>

          <LLink
            to="/voor-ondernemers"
            data-testid="nav-cta-ondernemers"
            className="hidden md:inline-flex items-center gap-1.5 bg-[#F3C300] hover:bg-[#e0b400] text-[#173A5E] font-[Poppins] font-semibold text-sm px-4 py-2 rounded-full shadow-sm transition"
          >
            <Store className="w-4 h-4" />
            {t("nav.voor_ondernemers")}
          </LLink>

          {/* Language switch */}
          <div className="relative">
            <button
              data-testid="lang-switch-btn"
              onClick={() => setLangOpen((s) => !s)}
              className="flex items-center gap-1 px-3 py-2 rounded-full text-sm font-[Poppins] font-semibold text-[#173A5E] hover:bg-[#F4F8FB] transition"
              aria-label={t("common.kies_taal")}
              aria-expanded={langOpen}
            >
              <span>{currentLang.display}</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            {langOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setLangOpen(false)}
                />
                <div
                  data-testid="lang-menu"
                  className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-[#E4EEF5] py-1 z-50"
                >
                  {LANGUAGES.map((l) => {
                    const active = l.code === lang;
                    return (
                      <button
                        key={l.code}
                        data-testid={`lang-option-${l.display}`}
                        onClick={() => {
                          switchLang(l.code);
                          setLangOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm font-[Inter] flex items-center justify-between transition ${
                          active
                            ? "bg-[#F4F8FB] text-[#2E9CD6] font-semibold"
                            : "text-[#173A5E] hover:bg-[#F4F8FB]"
                        }`}
                      >
                        <span>
                          <span className="font-semibold mr-2">{l.display}</span>
                          {t(`language.${l.code}`)}
                        </span>
                        {active && (
                          <span className="text-[10px] uppercase tracking-wide text-[#2E9CD6]">
                            •
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((s) => !s)}
            className="lg:hidden p-2 rounded-full hover:bg-[#F4F8FB] transition"
            aria-label="Menu"
          >
            {mobileOpen ? (
              <X className="w-6 h-6 text-[#173A5E]" />
            ) : (
              <Menu className="w-6 h-6 text-[#173A5E]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden border-t border-[#E4EEF5] bg-white"
        >
          <nav className="ll-container py-4 flex flex-col gap-1">
            <LNavLink
              to="/"
              end
              data-testid="mobile-nav-link-home"
              onClick={closeAll}
              className={({ isActive }) =>
                `px-3 py-3 rounded-lg font-[Poppins] font-medium text-base transition ${
                  isActive
                    ? "bg-[#F4F8FB] text-[#2E9CD6]"
                    : "text-[#173A5E] hover:bg-[#F4F8FB]"
                }`
              }
            >
              {t("nav.home")}
            </LNavLink>

            {/* Ontdekken uitklapbaar */}
            <div>
              <button
                type="button"
                data-testid="mobile-nav-ontdekken"
                onClick={() => setMobileOntdekkenOpen((s) => !s)}
                className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-base font-[Poppins] font-medium text-[#173A5E] hover:bg-[#F4F8FB] transition"
                aria-expanded={mobileOntdekkenOpen}
              >
                {t("nav.ontdekken")}
                <ChevronRight
                  className={`w-4 h-4 transition-transform ${
                    mobileOntdekkenOpen ? "rotate-90" : ""
                  }`}
                />
              </button>
              {mobileOntdekkenOpen && (
                <div className="pl-3 border-l-2 border-[#6BC0E8] ml-3 my-1 space-y-0.5">
                  {ONTDEKKEN.map((item) => (
                    <LNavLink
                      key={item.to}
                      to={item.to}
                      data-testid={`mobile-ontdekken-item-${item.key}`}
                      onClick={closeAll}
                      className={({ isActive }) =>
                        `block px-3 py-2.5 rounded-lg text-[15px] font-[Poppins] font-medium transition ${
                          isActive
                            ? "bg-[#F4F8FB] text-[#2E9CD6]"
                            : "text-[#3E5871] hover:bg-[#F4F8FB]"
                        }`
                      }
                    >
                      {t(`nav.${item.tKey}`)}
                    </LNavLink>
                  ))}
                </div>
              )}
            </div>

            {TOP_TAIL.map((item) => (
              <LNavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.key}`}
                onClick={closeAll}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg font-[Poppins] font-medium text-base transition ${
                    isActive
                      ? "bg-[#F4F8FB] text-[#2E9CD6]"
                      : "text-[#173A5E] hover:bg-[#F4F8FB]"
                  }`
                }
              >
                {t(`nav.${item.tKey}`)}
              </LNavLink>
            ))}

            <LLink
              to="/voor-ondernemers"
              data-testid="mobile-nav-cta-ondernemers"
              onClick={closeAll}
              className="mt-2 inline-flex items-center justify-center gap-1.5 bg-[#F3C300] hover:bg-[#e0b400] text-[#173A5E] font-[Poppins] font-semibold text-sm px-4 py-3 rounded-full shadow-sm transition"
            >
              <Store className="w-4 h-4" />
              {t("nav.voor_ondernemers")}
            </LLink>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
